import React, { useState, useEffect } from 'react';
import dashboardService from '../services/dashboardService';
import contractService from '../services/contractService';
import LoadingSpinner from '../components/common/LoadingSpinner';
import StatCards from '../components/dashboard/StatCards';
import Charts from '../components/dashboard/Charts';
import RecentActivity from '../components/dashboard/RecentActivity';
import { formatAmount } from '../utils/formatters';
import AmountDistributionTable from '../components/dashboard/AmountDistributionTable';



const DashboardPage = () => {
  const [distributionData, setDistributionData] = useState(null);
  const [stats, setStats] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeStatsView, setActiveStatsView] = useState('inProgress'); // 'inProgress' ou 'monthly'
  const [error, setError] = useState(null);
  const [filterOptions, setFilterOptions] = useState({ 
    agencies: [], 
    years: [], 
    months: []
  });
  const [filters, setFilters] = useState({
    agency: '',
    year: '',
    month: ''
  });

  // Charger les options de filtres (agences, années, mois disponibles)
  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        const result = await contractService.getFilterOptions();
        if (result.success) {
          setFilterOptions(result.data);
          
          // Si les mois ne sont pas retournés par l'API, créer une liste statique
          if (!result.data.months || result.data.months.length === 0) {
            const monthsList = [
              { value: '1', label: 'Janv' },
              { value: '2', label: 'Fév' },
              { value: '3', label: 'Mars' },
              { value: '4', label: 'Avril' },
              { value: '5', label: 'Mai' },
              { value: '6', label: 'Juin' },
              { value: '7', label: 'Juil' },
              { value: '8', label: 'Août' },
              { value: '9', label: 'Sept' },
              { value: '10', label: 'Oct' },
              { value: '11', label: 'Nov' },
              { value: '12', label: 'Déc' }
            ];
            
            setFilterOptions(prev => ({
              ...prev,
              months: monthsList
            }));
          }
          
          // Si les années ne sont pas retournées par l'API, créer une liste des 5 dernières années
          if (!result.data.years || result.data.years.length === 0) {
            const currentYear = new Date().getFullYear();
            const yearsList = [];
            for (let i = 0; i < 5; i++) {
              yearsList.push((currentYear - i).toString());
            }
            
            setFilterOptions(prev => ({
              ...prev,
              years: yearsList
            }));
          }
        }
      } catch (error) {
        console.error('Erreur lors du chargement des options de filtres:', error);
      }
    };
    
    loadFilterOptions();
  }, []);

  // Charger les statistiques et les données des graphiques
  useEffect(() => {
    let isMounted = true;
    
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Paramètres pour filtrer par agence, année et mois si nécessaire
        const params = {};
        if (filters.agency) params.agency = filters.agency;
        if (filters.year) params.year = filters.year;
        if (filters.month) params.month = filters.month;
        
        console.log('Début du chargement des statistiques avec filtres:', params);
        // Charger les statistiques
        const statsResult = await dashboardService.getStats(params);
        console.log('Résultat des statistiques:', statsResult);
        
        if (statsResult.success && isMounted) {
          setStats(statsResult.data);
        }
        

    // Charger les données de répartition
    try {
      const distributionResult = await dashboardService.getAmountDistribution(params);
      console.log('Résultat de la répartition des montants:', distributionResult);
      
      if (distributionResult.success && isMounted) {
        setDistributionData(distributionResult.data);
      }
    } catch (distributionError) {
      console.error('Erreur lors du chargement de la répartition des montants:', distributionError);
      
      if (isMounted) {
        // Ne pas interrompre le flux si cette partie échoue
        setDistributionData(null);
      }
    }




        try {
          console.log('Début du chargement des données des graphiques...');
          // Charger les données des graphiques
          const chartResult = await dashboardService.getChartData(params);
          console.log('Résultat des données des graphiques:', chartResult);
          
          if (chartResult.success && isMounted) {
            setChartData(chartResult.data);
          }
        } catch (chartError) {
          console.error('Erreur lors du chargement des données des graphiques:', chartError);
          
          if (isMounted) {
            setError('Erreur lors du chargement des données des graphiques. Les statistiques principales sont disponibles mais les graphiques ne peuvent pas être affichés.');
            // Même en cas d'erreur dans les graphiques, on continue d'afficher les stats principales
            setChartData(null);
          }
        }
      } catch (statsError) {
        console.error('Erreur lors du chargement des statistiques du tableau de bord:', statsError);
        
        if (isMounted) {
          setError('Erreur lors du chargement des données du tableau de bord.');
          setStats(null);
          setChartData(null);
        }
      } finally {
        if (isMounted) {
          console.log('Fin du chargement (success ou error)');
          setLoading(false);
        }
      }
    };
    
    fetchDashboardData();
    
    // Nettoyage pour éviter les problèmes de state update sur un composant démonté
    return () => {
      isMounted = false;
    };
  }, [filters.agency, filters.year, filters.month]);

  // Gérer le changement des filtres
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Debug: afficher l'état actuel du chargement
  console.log('État actuel:', { 
    loading, 
    error, 
    hasStats: !!stats, 
    hasChartData: !!chartData,
    filters 
  });

  if (loading) {
    return (
      <div className="container mx-auto p-4">
        <LoadingSpinner message="Chargement du tableau de bord..." />
        <p className="text-sm text-gray-500 text-center mt-4">
          Si le chargement persiste, veuillez rafraîchir la page.
        </p>
      </div>
    );
  }

  if (error && !stats) {
    return (
      <div className="container mx-auto p-4">
        <div className="bg-red-100 p-4 rounded-md text-red-700 mb-4">
          <h3 className="font-bold">Erreur</h3>
          <p>{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Rafraîchir la page
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4 bg-gray-50 min-h-screen">
      {error && (
        <div className="bg-yellow-100 p-4 rounded-md text-yellow-700 mb-4">
          <h3 className="font-bold">Attention</h3>
          <p>{error}</p>
        </div>
      )}
      
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-6 bg-white p-4 rounded-lg shadow">
        <h1 className="text-2xl font-bold text-gray-800 mb-4 lg:mb-0">Tableau de bord</h1>
        
        <div className="flex flex-col md:flex-row items-start md:items-center space-y-3 md:space-y-0 md:space-x-4 w-full lg:w-auto">
          {/* Filtre par agence */}
          <div className="flex flex-col w-full md:w-auto">
            <label htmlFor="agency" className="font-medium text-gray-700 mb-1">
              Agence:
            </label>
            <select
              id="agency"
              name="agency"
              value={filters.agency}
              onChange={handleFilterChange}
              className="form-select rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 transition-colors"
            >
              <option value="">Toutes les agences</option>
              {filterOptions.agencies && filterOptions.agencies.map((agency) => (
                <option key={agency} value={agency}>
                  Agence {agency}
                </option>
              ))}
            </select>
          </div>
          
          {/* Filtre par année */}
          <div className="flex flex-col w-full md:w-auto">
            <label htmlFor="year" className="font-medium text-gray-700 mb-1">
              Année:
            </label>
            <select
              id="year"
              name="year"
              value={filters.year}
              onChange={handleFilterChange}
              className="form-select rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 transition-colors"
            >
              <option value="">Toutes les années</option>
              {filterOptions.years && filterOptions.years.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>
          
          {/* Filtre par mois */}
          <div className="flex flex-col w-full md:w-auto">
            <label htmlFor="month" className="font-medium text-gray-700 mb-1">
              Mois:
            </label>
            <select
              id="month"
              name="month"
              value={filters.month}
              onChange={handleFilterChange}
              className="form-select rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 transition-colors"
            >
              <option value="">Tous les mois</option>
              {filterOptions.months && filterOptions.months.map((month) => (
                <option key={month.value} value={month.value}>
                  {month.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
      
      {/* Affichage des filtres actifs */}
      <div className="mb-4">
        <div className="flex flex-wrap items-center text-sm text-gray-600">
          <span className="mr-2 font-medium">Filtres actifs:</span>
          <span className="bg-indigo-100 text-indigo-800 px-3 py-1 rounded-full mr-2 mb-2">
            Agence: {filters.agency ? `Agence ${filters.agency}` : 'Toutes les agences'}
          </span>
          <span className="bg-indigo-100 text-indigo-800 px-3 py-1 rounded-full mr-2 mb-2">
            Année: {filters.year || 'Toutes les années'}
          </span>
          <span className="bg-indigo-100 text-indigo-800 px-3 py-1 rounded-full mr-2 mb-2">
            Mois: {filters.month ? filterOptions.months.find(m => m.value === filters.month)?.label || filters.month : 'Tous les mois'}
          </span>
          
          {/* Bouton pour réinitialiser les filtres */}
          {(filters.agency || filters.year || filters.month) && (
            <button
              onClick={() => setFilters({ agency: '', year: '', month: '' })}
              className="text-red-600 hover:text-red-800 text-sm underline"
            >
              Réinitialiser les filtres
            </button>
          )}
        </div>
      </div>
      
      {/* Cartes statistiques */}
      {stats && <StatCards stats={stats} />}
      
      {/* Section des statistiques par agence */}
      {stats && stats.byAgency && stats.byAgency.length > 0 && (
        <div className="bg-white shadow rounded-lg p-5 mb-6 hover:shadow-lg transition-shadow">
          <h2 className="text-lg font-semibold mb-4 text-gray-800">
            {filters.agency 
              ? `Statistiques détaillées - Agence ${filters.agency}` 
              : 'Statistiques par agence'}
          </h2>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Agence
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Nombre de contrats
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Montant décaissé
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Montant prévu
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Montant perçu
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Reste à percevoir
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Taux de recouvrement
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {stats.byAgency.map((agency) => (
                  <tr key={agency._id} className={filters.agency === agency._id ? 'bg-indigo-50' : 'hover:bg-gray-50'}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {agency._id}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{agency.count}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{formatAmount(agency.totalDisbursement)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{formatAmount(agency.totalExpected)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{formatAmount(agency.totalReceived)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {formatAmount(agency.totalExpected - agency.totalReceived)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className={`text-sm px-2 py-1 rounded-full inline-block ${
                        (agency.totalReceived / agency.totalExpected) * 100 >= 80 
                          ? 'bg-green-100 text-green-800' 
                          : (agency.totalReceived / agency.totalExpected) * 100 >= 50
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-red-100 text-red-800'
                      }`}>
                        {agency.totalExpected > 0 
                          ? `${((agency.totalReceived / agency.totalExpected) * 100).toFixed(2)}%` 
                          : 'N/A'}
                      </div>
                    </td>
                  </tr>
                ))}
                
                {/* Ligne de totaux */}
                <tr className="bg-gray-100 font-medium">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-bold text-gray-900">TOTAL</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-bold text-gray-900">
                      {stats.byAgency.reduce((sum, agency) => sum + agency.count, 0)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-bold text-gray-900">
                      {formatAmount(stats.byAgency.reduce((sum, agency) => sum + agency.totalDisbursement, 0))}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-bold text-gray-900">
                      {formatAmount(stats.byAgency.reduce((sum, agency) => sum + agency.totalExpected, 0))}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-bold text-gray-900">
                      {formatAmount(stats.byAgency.reduce((sum, agency) => sum + agency.totalReceived, 0))}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-bold text-gray-900">
                      {formatAmount(
                        stats.byAgency.reduce((sum, agency) => sum + agency.totalExpected, 0) - 
                        stats.byAgency.reduce((sum, agency) => sum + agency.totalReceived, 0)
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-bold text-gray-900">
                      {(() => {
                        const totalExpected = stats.byAgency.reduce((sum, agency) => sum + agency.totalExpected, 0);
                        const totalReceived = stats.byAgency.reduce((sum, agency) => sum + agency.totalReceived, 0);
                        const percentage = totalExpected > 0 ? (totalReceived / totalExpected) * 100 : 0;
                        const color = percentage >= 80 ? 'text-green-600' : percentage >= 50 ? 'text-yellow-600' : 'text-red-600';
                        
                        return totalExpected > 0 
                          ? <span className={color}>{`${percentage.toFixed(2)}%`}</span> 
                          : 'N/A';
                      })()}
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

{/* Nouvelle section de répartition des montants */}
{distributionData && <AmountDistributionTable data={distributionData} />}

      {/* Section des statistiques détaillées sur les contrats en cours */}
      
{/* Section des statistiques alternées (en cours / mensuelles) */}
{stats && (stats.inProgressStats || stats.byAgency) && (
  <div className="bg-white shadow rounded-lg p-5 mb-6 hover:shadow-lg transition-shadow">
    <div className="flex justify-between items-center mb-4">
      <h2 className="text-lg font-semibold text-gray-800">
        {activeStatsView === 'inProgress' 
          ? 'Statistiques des contrats en cours' 
          : 'Statistiques des contrats du mois'}
      </h2>
      
      {/* Bouton pour alterner entre les deux vues */}
      <button 
        onClick={() => setActiveStatsView(activeStatsView === 'inProgress' ? 'monthly' : 'inProgress')}
        className="px-4 py-2 bg-indigo-100 text-indigo-700 rounded-md hover:bg-indigo-200 transition-colors"
      >
        Voir {activeStatsView === 'inProgress' ? 'contrats du mois' : 'contrats en cours'}
      </button>
    </div>
    
    {/* Affichage conditionnel selon la vue active */}
    {activeStatsView === 'inProgress' ? (
      // Vue des contrats en cours (code existant)
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Tableau récapitulatif */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="text-md font-medium mb-3 text-gray-700">Récapitulatif</h3>
          
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-100">
              <tr>
                <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Métrique
                </th>
                <th scope="col" className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Valeur
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              <tr>
                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700">Nombre de contrats en cours</td>
                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 text-right font-medium">
                  {stats.inProgressStats.count}
                </td>
              </tr>
              <tr>
                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700">Montant total décaissé</td>
                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 text-right font-medium">
                  {formatAmount(stats.inProgressStats.totalDisbursement)}
                </td>
              </tr>
              <tr>
                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700">Montant total prévu</td>
                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 text-right font-medium">
                  {formatAmount(stats.inProgressStats.totalExpected)}
                </td>
              </tr>
              
              <tr>
                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700">Montant total perçu</td>
                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 text-right font-medium">
                  {formatAmount(stats.inProgressStats.totalReceived)}
                </td>
              </tr>
              <tr>
                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700">Reste à percevoir</td>
                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 text-right font-medium">
                  {formatAmount(stats.inProgressStats.totalExpected - stats.inProgressStats.totalReceived)}
                </td>
              </tr>            
              
              <tr>
                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700">Poids total bijou</td>
                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 text-right font-medium">
                  {stats.inProgressStats.totalJewelryWeight.toFixed(2)} g
                </td>
              </tr>
              <tr>
                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700">Poids total CDVA</td>
                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 text-right font-medium">
                  {stats.inProgressStats.totalCDVAWeight.toFixed(2)} g
                </td>
              </tr>
              <tr>
                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700">Poids total CDD</td>
                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 text-right font-medium">
                  {stats.inProgressStats.totalCDDWeight.toFixed(2)} g
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        
        {/* Statistiques par agence */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="text-md font-medium mb-3 text-gray-700">Répartition par agence</h3>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-100">
                <tr>
                  <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Agence
                  </th>
                  <th scope="col" className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contrats
                  </th>
                  <th scope="col" className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Poids bijou
                  </th>
                  <th scope="col" className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Poids CDVA
                  </th>
                  <th scope="col" className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Poids CDD
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {stats.inProgressStats.byAgency && stats.inProgressStats.byAgency.map((agency) => (
                  <tr key={agency._id}>
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 font-medium">
                      {agency._id}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 text-right">
                      {agency.count}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 text-right">
                      {agency.jewelryWeight.toFixed(2)} g
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 text-right">
                      {agency.cdvaWeight.toFixed(2)} g
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 text-right">
                      {agency.cddWeight.toFixed(2)} g
                    </td>
                  </tr>
                ))}
                
                {/* Ligne de totaux */}
                <tr className="bg-gray-100 font-medium">
                  <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                    TOTAL
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 text-right">
                    {stats.inProgressStats.count}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 text-right">
                    {stats.inProgressStats.totalJewelryWeight.toFixed(2)} g
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 text-right">
                    {stats.inProgressStats.totalCDVAWeight.toFixed(2)} g
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 text-right">
                    {stats.inProgressStats.totalCDDWeight.toFixed(2)} g
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        
        {/* Graphique répartition des poids */}
        <div className="lg:col-span-2 bg-gray-50 p-4 rounded-lg">
          <h3 className="text-md font-medium mb-3 text-gray-700">Répartition par types de poids</h3>
          
          <div className="flex flex-col md:flex-row items-center justify-center space-y-4 md:space-y-0 md:space-x-6">
            <div className="flex items-center space-x-2 bg-white p-3 rounded-lg shadow-sm">
              <div className="h-12 w-12 rounded-full bg-indigo-500 flex items-center justify-center text-white font-medium">
                {stats.inProgressStats.count}
              </div>
              <div>
                <div className="text-xs text-gray-500">Contrats en cours</div>
                <div className="text-sm font-medium">
                  {((stats.inProgressStats.count / stats.totalContracts) * 100).toFixed(1)}% du total
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-2 bg-white p-3 rounded-lg shadow-sm">
              <div className="h-12 w-12 rounded-full bg-yellow-500 flex items-center justify-center text-white font-medium">
                <span className="text-xs">Bijou</span>
              </div>
              <div>
                <div className="text-xs text-gray-500">Poids bijou total</div>
                <div className="text-sm font-medium">{stats.inProgressStats.totalJewelryWeight.toFixed(2)} g</div>
              </div>
            </div>
            
            <div className="flex items-center space-x-2 bg-white p-3 rounded-lg shadow-sm">
              <div className="h-12 w-12 rounded-full bg-green-500 flex items-center justify-center text-white font-medium">
                <span className="text-xs">CDVA</span>
              </div>
              <div>
                <div className="text-xs text-gray-500">Poids CDVA total</div>
                <div className="text-sm font-medium">{stats.inProgressStats.totalCDVAWeight.toFixed(2)} g</div>
              </div>
            </div>
            
            <div className="flex items-center space-x-2 bg-white p-3 rounded-lg shadow-sm">
              <div className="h-12 w-12 rounded-full bg-pink-500 flex items-center justify-center text-white font-medium">
                <span className="text-xs">CDD</span>
              </div>
              <div>
                <div className="text-xs text-gray-500">Poids CDD total</div>
                <div className="text-sm font-medium">{stats.inProgressStats.totalCDDWeight.toFixed(2)} g</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    ) : (
      // Vue des contrats du mois (nouvelle section)
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Tableau récapitulatif des contrats du mois */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="text-md font-medium mb-3 text-gray-700">Récapitulatif du mois</h3>
          
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-100">
              <tr>
                <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Métrique
                </th>
                <th scope="col" className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Valeur
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              <tr>
                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700">Nombre de contrats</td>
                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 text-right font-medium">
                  {stats.totalContracts || 0}
                </td>
              </tr>
              <tr>
                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700">Montant total décaissé</td>
                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 text-right font-medium">
                  {formatAmount(stats.financialStats?.totalDisbursement || 0)}
                </td>
              </tr>
              <tr>
                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700">Montant total prévu</td>
                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 text-right font-medium">
                  {formatAmount(stats.financialStats?.totalExpected || 0)}
                </td>
              </tr>
              <tr>
                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700">Poids total bijou</td>
                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 text-right font-medium">
                  {(stats.financialStats?.totalJewelryWeight || 0).toFixed(2)} g
                </td>
              </tr>
              <tr>
                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700">Poids total CDVA</td>
                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 text-right font-medium">
                  {(stats.financialStats?.totalCDVAWeight || 0).toFixed(2)} g
                </td>
              </tr>
              <tr>
                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700">Poids total CDD</td>
                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 text-right font-medium">
                  {(stats.financialStats?.totalCDDWeight || 0).toFixed(2)} g
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        
        {/* Statistiques par agence pour le mois */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="text-md font-medium mb-3 text-gray-700">Répartition par agence</h3>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-100">
                <tr>
                  <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Agence
                  </th>
                  <th scope="col" className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contrats
                  </th>
                  <th scope="col" className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Montant décaissé
                  </th>
                  <th scope="col" className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Montant prévu
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {stats.byAgency && stats.byAgency.map((agency) => (
                  <tr key={agency._id} className={filters.agency === agency._id ? 'bg-indigo-50' : ''}>
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 font-medium">
                      {agency._id}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 text-right">
                      {agency.count}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 text-right">
                      {formatAmount(agency.totalDisbursement)}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 text-right">
                      {formatAmount(agency.totalExpected)}
                    </td>
                  </tr>
                ))}
                
                {/* Ligne de totaux */}
                <tr className="bg-gray-100 font-medium">
                  <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                    TOTAL
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 text-right">
                    {stats.totalContracts}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 text-right">
                    {formatAmount(stats.financialStats?.totalDisbursement || 0)}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 text-right">
                    {formatAmount(stats.financialStats?.totalExpected || 0)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        
        {/* Graphique des informations du mois */}
        <div className="lg:col-span-2 bg-gray-50 p-4 rounded-lg">
          <h3 className="text-md font-medium mb-3 text-gray-700">Répartition des montants</h3>
          
          <div className="flex flex-col md:flex-row items-center justify-center space-y-4 md:space-y-0 md:space-x-6">
            <div className="flex items-center space-x-2 bg-white p-3 rounded-lg shadow-sm">
              <div className="h-12 w-12 rounded-full bg-indigo-500 flex items-center justify-center text-white font-medium">
                <span className="text-xs">Décaissé</span>
              </div>
              <div>
                <div className="text-xs text-gray-500">Montant décaissé</div>
                <div className="text-sm font-medium">{formatAmount(stats.financialStats?.totalDisbursement || 0)}</div>
              </div>
            </div>
            
            <div className="flex items-center space-x-2 bg-white p-3 rounded-lg shadow-sm">
              <div className="h-12 w-12 rounded-full bg-yellow-500 flex items-center justify-center text-white font-medium">
                <span className="text-xs">Prévu</span>
              </div>
              <div>
                <div className="text-xs text-gray-500">Montant prévu</div>
                <div className="text-sm font-medium">{formatAmount(stats.financialStats?.totalExpected || 0)}</div>
              </div>
            </div>
            
            <div className="flex items-center space-x-2 bg-white p-3 rounded-lg shadow-sm">
              <div className="h-12 w-12 rounded-full bg-green-500 flex items-center justify-center text-white font-medium">
                <span className="text-xs">Ratio</span>
              </div>
              <div>
                <div className="text-xs text-gray-500">Taux de recouvrement</div>
                <div className="text-sm font-medium">{stats.recoveryRate}%</div>
              </div>
            </div>
            
            <div className="flex items-center space-x-2 bg-white p-3 rounded-lg shadow-sm">
              <div className="h-12 w-12 rounded-full bg-purple-500 flex items-center justify-center text-white font-medium">
                <span className="text-xs">Poids</span>
              </div>
              <div>
                <div className="text-xs text-gray-500">Poids bijou total</div>
                <div className="text-sm font-medium">{(stats.financialStats?.totalJewelryWeight || 0).toFixed(2)} g</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )}
  </div>
)}



      {/* Graphiques */}
      {chartData && <Charts data={chartData} />}
      
      {/* Si les graphiques ne sont pas disponibles mais les stats le sont */}
      {stats && !chartData && !loading && (
        <div className="bg-white shadow rounded-lg p-5 mb-6">
          <h3 className="text-lg font-semibold mb-4 text-gray-800">Graphiques</h3>
          <div className="p-8 text-center text-gray-500">
            Les graphiques ne peuvent pas être affichés actuellement.
            <div className="mt-4">
              <button 
                onClick={() => window.location.reload()} 
                className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
              >
                Rafraîchir la page
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Activités récentes */}
      <div className="mt-6">
        {stats && stats.recentActivities && (
          <RecentActivity activities={stats.recentActivities} />
        )}
      </div>
    </div>
  );
};

export default DashboardPage;